import React from "react";

/**
 * Glowing football — premium cinematic soccer ball.
 *
 * Geometry: classic truncated-icosahedron front projection
 *   — 1 centre pentagon + 5 surrounding hexagons + outer circle.
 *
 * Style: COLOURED glowing seams (no white outlines), soft outer bloom,
 * premium / elegant — never aggressive or gaming.  The seams ARE the glow.
 *
 * Used both inside the BrandLogo and as floating decoration so every
 * football on the site reads as the same identity.
 */
const GlowingFootball = ({
  size = 180,
  mobileSize,
  color = "#3DBFFF",
  className = "",
  delay = 0,
  speed = 80,
  intensity = 1,
}) => {
  const id = React.useId();
  // Responsive size — falls back to 60 % on screens narrower than 768 px so
  // floating footballs never overpower mobile / iPhone Safari layouts.
  const responsiveSize = mobileSize ?? Math.round(size * 0.6);

  // Centre pentagon — vertices at angles −90°, −18°, 54°, 126°, 198°
  const centrePentagon = (() => {
    const r = 8.5;
    return [-90, -18, 54, 126, 198]
      .map((deg) => {
        const a = (deg * Math.PI) / 180;
        return `${(50 + Math.cos(a) * r).toFixed(2)},${(50 + Math.sin(a) * r).toFixed(2)}`;
      })
      .join(" ");
  })();

  // Five hexagons sitting on each edge of the centre pentagon,
  // oriented so one edge faces the centre.
  const hexagons = [-54, 18, 90, 162, 234].map((deg) => {
    const θ = (deg * Math.PI) / 180;
    const cx = 50 + Math.cos(θ) * 18;
    const cy = 50 + Math.sin(θ) * 18;
    const r = 9.4;
    const points = [];
    for (let k = 0; k < 6; k++) {
      const va = θ + Math.PI + ((k * 60 + 30) * Math.PI) / 180;
      points.push(
        `${(cx + Math.cos(va) * r).toFixed(2)},${(cy + Math.sin(va) * r).toFixed(2)}`,
      );
    }
    return points.join(" ");
  });

  return (
    <div
      aria-hidden
      className={`pointer-events-none absolute select-none ${className}`}
      style={{
        width: `clamp(${responsiveSize}px, ${(size / 1440) * 100}vw, ${size}px)`,
        height: `clamp(${responsiveSize}px, ${(size / 1440) * 100}vw, ${size}px)`,
        animation: `eu-football-drift ${24 + speed / 6}s ease-in-out infinite`,
        animationDelay: `${delay}s`,
        // Soft, layered outward bloom — premium, never harsh
        filter: `drop-shadow(0 0 ${14 * intensity}px ${color}cc) drop-shadow(0 0 ${
          38 * intensity
        }px ${color}88) drop-shadow(0 0 ${88 * intensity}px ${color}55)`,
      }}
    >
      <div
        style={{
          width: "100%",
          height: "100%",
          animation: `eu-football-spin ${speed}s linear infinite`,
          animationDelay: `${delay}s`,
        }}
      >
        <svg
          viewBox="0 0 100 100"
          width="100%"
          height="100%"
          xmlns="http://www.w3.org/2000/svg"
          style={{ overflow: "visible" }}
        >
          <defs>
            <radialGradient id={`gf-core-${id}`} cx="50%" cy="42%" r="65%">
              <stop offset="0%" stopColor="#15082A" stopOpacity="1" />
              <stop offset="55%" stopColor="#0A0418" stopOpacity="1" />
              <stop offset="100%" stopColor="#05020E" stopOpacity="1" />
            </radialGradient>
            <radialGradient id={`gf-tint-${id}`} cx="50%" cy="50%" r="50%">
              <stop offset="0%" stopColor={color} stopOpacity="0.10" />
              <stop offset="70%" stopColor={color} stopOpacity="0.04" />
              <stop offset="100%" stopColor={color} stopOpacity="0" />
            </radialGradient>
            <filter
              id={`gf-bloom-${id}`}
              x="-50%"
              y="-50%"
              width="200%"
              height="200%"
            >
              <feGaussianBlur stdDeviation="0.55" result="b" />
              <feMerge>
                <feMergeNode in="b" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
          </defs>

          {/* Sphere body — deep cosmic core */}
          <circle cx="50" cy="50" r="44" fill={`url(#gf-core-${id})`} />
          {/* Subtle colour tint inside the sphere */}
          <circle cx="50" cy="50" r="44" fill={`url(#gf-tint-${id})`} />

          {/* Glowing coloured seams — the seams ARE the light */}
          <g
            stroke={color}
            strokeWidth="1.35"
            strokeOpacity="0.92"
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
            filter={`url(#gf-bloom-${id})`}
          >
            <polygon points={centrePentagon} />
            {hexagons.map((pts, i) => (
              <polygon key={i} points={pts} opacity="0.88" />
            ))}
            <circle cx="50" cy="50" r="44" strokeOpacity="0.7" />
          </g>

          {/* Soft inner highlight — a single white pinprick gives the sphere
              its premium liquid-glass feel without adding any noise */}
          <circle
            cx="38"
            cy="34"
            r="2.2"
            fill="#ffffff"
            fillOpacity="0.22"
            filter={`url(#gf-bloom-${id})`}
          />
        </svg>
      </div>
    </div>
  );
};

export default GlowingFootball;
